import React, {FC, useState, useEffect, useRef} from 'react';
import {Elements, PaymentElement, useStripe, useElements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import {Box, Flex, Stack, Text, useToast} from '@chakra-ui/react';
import {MainButton} from "./Buttons/MainButton";
import {useDispatch} from 'react-redux';
import {StripeController} from "../controllers/StripeController";
import {PaymentRequest} from '../shared/entities/Stripe/Stripe';
import {calculateTotalChargedWithStripeFee, getStripeCommissionLabel} from '../shared/utils/stripeCommission';

interface StripePaymentProps {
    amount: number;
    currency?: string;
    /** После синхронизации с бэком; `paymentIntentId` есть, если оплата завершилась без редиректа. */
    onSuccess: (paymentIntentId?: string) => void;
    onError: (error: string) => void;
    onCancel: () => void;
    /** Без отдельной рамки и заголовка — для показа внутри модалки */
    inModal?: boolean;
    orderData?: {
        orderId?: string;
        poNumber?: string;
        namePo?: string;
        dealershipId?: number | string;
        customerEmail?: string;
        customerName?: string;
        description?: string;
    };
}

// Форма оплаты
const PaymentForm: FC<{
    amount: number;
    totalWithFee: number;
    onSuccess: (paymentIntentId?: string) => void;
    onError: (error: string) => void;
    onCancel: () => void;
    orderData?: StripePaymentProps['orderData'];
    clientSecret: string;
}> = ({amount, totalWithFee, onSuccess, onError, onCancel, orderData, clientSecret}) => {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useDispatch();
    const stripeController = new StripeController(dispatch);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isPaymentMethodReady, setIsPaymentMethodReady] = useState(false);
    const toast = useToast();

    // Обработчик изменения состояния платежных методов
    const handlePaymentElementChange = (event: any) => {
        // event.complete означает, что все необходимые поля заполнены корректно
        // (номер карты, срок, CVC и т.д.)
        setIsPaymentMethodReady(event.complete);
    };

    // Обработчик готовности Stripe Elements
    const handlePaymentElementReady = (element: any) => {
        console.log("Payment element ready");
        // При готовности проверяем, есть ли уже заполненные данные
        // (если браузер сохранил карту)
        setTimeout(async () => {
            if (elements && stripe) {
                const {error} = await elements.submit();
                if (!error && elements.getElement('payment')) {
                    // Дополнительная проверка
                }
            }
        }, 100);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            toast({
                title: 'Stripe not initialized',
                status: 'error',
                duration: 3000,
            });
            return;
        }

        // Проверяем, что платежный метод выбран и валиден
        if (!isPaymentMethodReady) {
            toast({
                title: 'Please enter payment details',
                status: 'warning',
                duration: 3000,
            });
            return;
        }

        setIsProcessing(true);

        try {
            const confirmResult = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/payment-success`,
                },
                // Для card-платежей остаемся на странице и продолжаем JS-поток,
                // чтобы отправить confirm-payment-success (orderId + paymentIntentId).
                redirect: 'if_required',
            }) as {
                error?: {message?: string; type?: string};
                paymentIntent?: {id: string} | null;
            };

            if (confirmResult.error) {
                throw confirmResult.error;
            }

            const paymentIntentId = confirmResult.paymentIntent?.id;
            const orderIdRaw = orderData?.orderId;
            if (orderIdRaw != null && orderIdRaw !== '') {
                const orderId = Number(orderIdRaw);
                if (!Number.isFinite(orderId)) {
                    throw new Error('Invalid order id');
                }
                if (!paymentIntentId) {
                    throw new Error('Missing payment intent id');
                }
                const res = await stripeController.confirmPaymentSuccess({orderId, paymentIntentId});
                if (!res?.success) {
                    throw new Error(res?.message || 'Could not update order on server');
                }
            }

            onSuccess(paymentIntentId);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Payment failed';
            onError(errorMessage);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Box mb={4}>
                <PaymentElement
                    onChange={handlePaymentElementChange}
                    onReady={handlePaymentElementReady}
                />
            </Box>

            {/* Отображение детализации комиссии */}
            <Box mb={4} p={3} bg="gray.100" borderRadius="md">
                <Flex justifyContent="space-between" mb={2}>
                    <Text fontSize="sm">Subtotal:</Text>
                    <Text fontSize="sm" fontWeight="medium">${amount.toFixed(2)}</Text>
                </Flex>
                <Flex justifyContent="space-between" mb={2}>
                    <Text fontSize="sm" color="gray.600">{`Fee (${getStripeCommissionLabel()}):`}</Text>
                    <Text fontSize="sm" color="gray.600">${(totalWithFee - amount).toFixed(2)}</Text>
                </Flex>
                <Flex justifyContent="space-between" pt={2} borderTop="1px solid" borderColor="gray.300">
                    <Text fontWeight="bold">Total:</Text>
                    <Text fontWeight="bold" color="blue.600">${totalWithFee.toFixed(2)}</Text>
                </Flex>
            </Box>

            <Stack
                spacing={3}
                direction={{base: "column", sm: "row"}}
                width="100%"
                align="stretch"
            >
                <MainButton
                    flex={1}
                    minW={0}
                    onClick={onCancel}
                    background="gray.500"
                    color="white"
                    _hover={{background: "gray.600"}}
                >
                    Cancel
                </MainButton>
                <MainButton
                    flex={1}
                    minW={0}
                    type="submit"
                    disabled={!stripe || isProcessing || !isPaymentMethodReady}
                    background="blue.500"
                    color="white"
                    _hover={{background: isPaymentMethodReady ? "blue.600" : "blue.500"}}
                    opacity={!isPaymentMethodReady ? 0.6 : 1}
                    cursor={!isPaymentMethodReady ? "not-allowed" : "pointer"}
                >
                    {isProcessing ? 'Processing...' : `Pay $${totalWithFee.toFixed(2)}`}
                </MainButton>
            </Stack>
        </form>
    );
};

// Основной компонент
const StripePayment: FC<StripePaymentProps> = ({
                                                   amount,
                                                   currency = 'usd',
                                                   onSuccess,
                                                   onError,
                                                   onCancel,
                                                   inModal = false,
                                                   orderData
                                               }) => {
    const [stripePromise, setStripePromise] = useState<any>(null);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [isInitializing, setIsInitializing] = useState(false);
    const toast = useToast();
    const dispatch = useDispatch();
    const stripeController = new StripeController(dispatch);

    // Рассчитываем сумму с комиссией
    const totalWithFee = calculateTotalChargedWithStripeFee(amount);
    // Сумма в центах для бэкенда (уже с комиссией)
    const amountInCentsWithFee = totalWithFee;

    const isStripeInitialized = useRef(false);
    const isMounted = useRef(true);
    const isInitializingRef = useRef(false);

    console.log("StripePayment props:", {amount, totalWithFee, currency, orderData});

    // Инициализация Stripe
    useEffect(() => {
        if (isStripeInitialized.current) return;

        const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
        console.log("Stripe public key:", stripePublicKey);

        if (stripePublicKey) {
            setStripePromise(loadStripe(stripePublicKey));
            isStripeInitialized.current = true;
        } else {
            console.error('Stripe public key is not configured');
        }

        return () => {
            isMounted.current = false;
        };
    }, []);

    // Получение clientSecret — передаем уже сумму С КОМИССИЕЙ
    useEffect(() => {
        if (!stripePromise || amount <= 0 || !isMounted.current) return;
        if (isInitializingRef.current) {
            console.log("Already initializing, skipping...");
            return;
        }

        const initializePayment = async () => {
            isInitializingRef.current = true;
            setIsInitializing(true);

            try {
                // ВАЖНО: передаем сумму УЖЕ с комиссией!
                const paymentRequest: PaymentRequest = {
                    amount: amountInCentsWithFee, // сумма с учётом Stripe fee (в основной валюте, USD и т.д.)
                    currency: currency,
                    orderId: Number(orderData?.orderId),
                    poNumber: orderData?.poNumber,
                    namePo: orderData?.namePo,
                    dealershipId:
                        orderData?.dealershipId != null ? String(orderData.dealershipId) : undefined,
                    customerEmail: orderData?.customerEmail,
                    customerName: orderData?.customerName,
                    description: orderData?.description || "Payment for order"
                };

                console.log("Creating payment intent with amount (including fee):", paymentRequest.amount);

                const response = await stripeController.createPaymentIntent(paymentRequest);

                if (!isMounted.current) return;

                if (response.success && response.clientSecret) {
                    setClientSecret(response.clientSecret);
                } else {
                    throw new Error(response.message || 'Failed to initialize payment');
                }
            } catch (error) {
                if (!isMounted.current) return;
                const errorMessage = error instanceof Error ? error.message : 'Payment initialization failed';
                console.error("Payment initialization error:", errorMessage);
                onError(errorMessage);
                onCancel();
            } finally {
                if (isMounted.current) {
                    setIsInitializing(false);
                }
                isInitializingRef.current = false;
            }
        };

        initializePayment();
    }, [amountInCentsWithFee, currency, orderData?.orderId, orderData?.poNumber, orderData?.namePo, orderData?.dealershipId, stripePromise]);

    if (!stripePromise && isStripeInitialized.current === false) {
        return (
            <Box p={4} textAlign="center">
                <Text>Loading payment gateway...</Text>
            </Box>
        );
    }

    if (!stripePromise) {
        return (
            <Box p={4} textAlign="center">
                <Text color="red.500">Payment system is not configured. Please contact support.</Text>
            </Box>
        );
    }

    if (isInitializing) {
        return (
            <Box p={4} textAlign="center">
                <Text>Preparing payment...</Text>
            </Box>
        );
    }

    if (!clientSecret) {
        return (
            <Box p={4} textAlign="center">
                <Text color="red.500">Unable to initialize payment. Please try again.</Text>
            </Box>
        );
    }

    const options = {
        clientSecret,
        wallets: {
            applePay: 'auto' as const,
            googlePay: 'auto' as const
        }
    } as const;

    const form = (
        <Elements stripe={stripePromise} options={options}>
            <PaymentForm
                amount={amount}
                totalWithFee={totalWithFee}
                onSuccess={onSuccess}
                onError={onError}
                onCancel={onCancel}
                orderData={orderData}
                clientSecret={clientSecret}
            />
        </Elements>
    );

    if (inModal) {
        return <Box>{form}</Box>;
    }

    return (
        <Box mt={4} p={4} borderWidth="1px" borderRadius="lg" bg="gray.50">
            <Text fontWeight="bold" mb={3} fontSize="lg">
                Credit Card Payment
            </Text>
            {form}
        </Box>
    );
};

export default StripePayment;